import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, FileText, Sparkles, ArrowRight, MapPin, LayoutDashboard, BookOpen, User, Award, Printer, Download, Eye } from "lucide-react";
import api from "../lib/api";
import PortalShell from "./PortalShell";
import StudentApplications from "./StudentApplications";
import StudentLearn from "./StudentLearn";
import StudentProfile from "./StudentProfile";
import StudentGraduate from "./StudentGraduate";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { printAppDoc, downloadAppDoc, displayStatus } from "../lib/appDoc";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-800",
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  Qualified: "bg-purple-100 text-purple-700", Applied: "bg-cyan-100 text-cyan-700",
  Admitted: "bg-green-100 text-green-700", Closed: "bg-slate-100 text-slate-600", Lost: "bg-red-100 text-red-700",
};

export default function StudentPortal() {
  const [data, setData] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  useEffect(() => { api.get("/portal/student/overview").then((r) => setData(r.data)).catch(() => {}); }, []);

  const leadDoc = (a) => ({
    docLabel: "Enquiry / Application", heading: a.course || "General Enquiry", student: data?.profile?.name, status: a.status,
    rows: [
      ["Preferred country", a.country], ["Level", a.level], ["Intake", a.intake],
      ["Email", a.email], ["Phone", a.phone],
      ["Created", a.created_at ? new Date(a.created_at).toLocaleDateString() : ""],
      ["Message", a.message || a.notes],
    ],
  });

  return (
    <PortalShell allow="student">
      <h1 className="font-display text-3xl font-bold text-[#012a1e]">Hello, {data?.profile?.name?.split(" ")[0] || ""} 👋</h1>
      <p className="text-slate-500 mt-1">Track your study-abroad applications and get expert guidance.</p>

      <div className="flex flex-wrap gap-1 bg-white border border-slate-100 rounded-2xl sm:rounded-full p-1 mt-5 w-full sm:w-fit">
        {[{ k: "overview", l: "Overview", i: LayoutDashboard }, { k: "profile", l: "My Profile", i: User }, { k: "applications", l: "Applications", i: FileText }, { k: "learn", l: "Learn", i: BookOpen }, { k: "graduate", l: "Certificates", i: Award }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`student-tab-${t.k}`} className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-colors ${tab === t.k ? "bg-[#004030] text-white" : "text-[#004030]"}`}>
            <t.i size={16} /> {t.l}
          </button>
        ))}
      </div>

      {tab === "profile" ? (
        <div className="mt-6"><StudentProfile /></div>
      ) : tab === "applications" ? (
        <div className="mt-6"><StudentApplications /></div>
      ) : tab === "learn" ? (
        <div className="mt-6"><StudentLearn /></div>
      ) : tab === "graduate" ? (
        <div className="mt-6"><StudentGraduate /></div>
      ) : (
      <>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="w-11 h-11 rounded-xl bg-[#004030] text-[#e0c05a] grid place-items-center"><FileText size={20} /></div>
          <p className="font-display text-3xl font-bold text-[#012a1e] mt-3">{data?.counts?.applications ?? 0}</p>
          <p className="text-slate-500 text-sm">Applications / Enquiries</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="w-11 h-11 rounded-xl bg-[#c9a227] text-[#013d2b] grid place-items-center"><GraduationCap size={20} /></div>
          <p className="font-semibold text-[#012a1e] mt-3">{data?.profile?.interest || "Not set"}</p>
          <p className="text-slate-500 text-sm">Course of interest</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="w-11 h-11 rounded-xl bg-[#0a5540] text-[#e0c05a] grid place-items-center"><MapPin size={20} /></div>
          <p className="font-semibold text-[#012a1e] mt-3">{data?.profile?.country || "Exploring"}</p>
          <p className="text-slate-500 text-sm">Preferred destination</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 mt-6">
        <div className="p-5 border-b border-slate-100"><h2 className="font-semibold text-[#012a1e]">My Applications</h2></div>
        <div className="divide-y divide-slate-100">
          {data?.applications?.length ? data.applications.map((a) => (
            <div key={a.id} data-testid={`overview-app-${a.id}`} className="flex items-center justify-between gap-3 px-5 py-3.5 flex-wrap">
              <div className="min-w-[140px]"><p className="font-medium text-[#012a1e]">{a.course || "General enquiry"}</p><p className="text-xs text-slate-500">{a.country || "—"} · {new Date(a.created_at).toLocaleDateString()}</p></div>
              <div className="flex items-center gap-2 ml-auto">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[displayStatus(a.status)] || STATUS_COLORS[a.status] || "bg-slate-100"}`}>{displayStatus(a.status)}</span>
                <button data-testid={`overview-view-${a.id}`} onClick={() => setViewLead(a)} title="View" className="w-8 h-8 grid place-items-center rounded-full border border-slate-200 text-[#004030] hover:bg-[#f0f6f3]"><Eye size={14} /></button>
                <button data-testid={`overview-print-${a.id}`} onClick={() => printAppDoc(leadDoc(a))} title="Print" className="w-8 h-8 grid place-items-center rounded-full border border-slate-200 text-[#004030] hover:bg-[#f0f6f3]"><Printer size={14} /></button>
                <button data-testid={`overview-download-${a.id}`} onClick={() => downloadAppDoc(leadDoc(a))} title="Download PDF" className="w-8 h-8 grid place-items-center rounded-full border border-slate-200 text-[#004030] hover:bg-[#f0f6f3]"><Download size={14} /></button>
              </div>
            </div>
          )) : (
            <div className="px-5 py-10 text-center">
              <p className="text-slate-400">No applications yet.</p>
              <Link to="/contact"><Button className="mt-3 bg-[#004030] text-white rounded-full px-6">Start an enquiry <ArrowRight size={16} className="ml-1" /></Button></Link>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 bg-[#004030] rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Sparkles className="text-[#e0c05a]" size={24} />
          <div><p className="text-white font-semibold">Need guidance?</p><p className="text-white/70 text-sm">Chat with Vidya AI or book a free counsellor session.</p></div>
        </div>
        <Link to="/"><Button className="bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full px-6 font-bold">Explore &amp; Ask Vidya</Button></Link>
      </div>
      </>
      )}

      <Dialog open={!!viewLead} onOpenChange={(o) => { if (!o) setViewLead(null); }}>
        <DialogContent className="max-w-md" data-testid="overview-view-dialog">
          {viewLead && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl text-[#012a1e]">{viewLead.course || "General enquiry"}</DialogTitle></DialogHeader>
              <span className={`inline-block w-fit text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[displayStatus(viewLead.status)] || STATUS_COLORS[viewLead.status] || "bg-slate-100"}`}>{displayStatus(viewLead.status)}</span>
              <div className="mt-2 divide-y divide-slate-100 text-sm">
                {[["Preferred country", viewLead.country], ["Level", viewLead.level], ["Intake", viewLead.intake], ["Email", viewLead.email], ["Phone", viewLead.phone], ["Created", viewLead.created_at ? new Date(viewLead.created_at).toLocaleString() : ""], ["Message", viewLead.message || viewLead.notes]].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2"><span className="text-slate-500">{k}</span><span className="font-medium text-[#012a1e] text-right">{v}</span></div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button onClick={() => printAppDoc(leadDoc(viewLead))} variant="outline" className="flex-1 rounded-full"><Printer size={15} className="mr-1" /> Print</Button>
                <Button onClick={() => downloadAppDoc(leadDoc(viewLead))} className="flex-1 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full"><Download size={15} className="mr-1" /> Download PDF</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PortalShell>
  );
}
